/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from "react";
import { GlobalStore } from "../../../../store/global/GlobalStore";
import { ReactComponent as SafeIcon } from "../../../../img/approval_svgs/Safe.svg";
import { Token } from "../../../../store/global/TokenEnum";
import { getTokenIcon } from "../../../../utils/tokenIconUtils";
import { Text } from "../../../general/Text";
import { css } from "@emotion/react";
import { useAccount } from "wagmi";
import { ApprovalRow } from "./ApproveFlow/ApprovalRow";
import { ApprovalStore } from "../../../../store/approvals/ApprovalsStore";
import { useApprovalTarget } from "../../../../hooks/allowances/useApprovalTarget";
import { TokenFlow } from "../../../../hooks/allowances/useTokensForApproval";
import styled from "@emotion/styled";
import { ReactComponent as StarIcon } from "../../../../img/butter_svgs/Star.svg";
import { ReactComponent as ApprovedIcon } from "../../../../img/approval_svgs/approved.svg";
import { FlexContainer } from "../../../../containers/FlexContainer";
import { NAV_LINKS } from "../../../../config/consts";
import MetaMaskTip from "./elements/MetaMaskTip";
import SignatureRequest from "./SignatureRequest";
import { IconContainer } from "./elements/IconContainer";
import { Button } from "../../../general/Button/Button";
import { ApprovalsHeader } from "./elements/ApprovalsHeader";
import { Link } from "react-router-dom";

export const APPROVALS_MODAL_CLOSE_TIMEOUT = 2500;

export interface ApprovalProps {
  onDone: () => void;
}
export const Approvals: React.FC<ApprovalProps> = props => {
  const network = GlobalStore.useState(s => s.network);
  const { tokensToApprove, requiredSignatures, permitType, tokenLists } = ApprovalStore.useState(
    s => s
  );
  const tokenList = tokenLists?.[network.id];
  const { toApprove, toRevoke } = ApprovalStore.useState(s => {
    return {
      toApprove: s.tokensByFlow.approve,
      toRevoke: s.tokensByFlow.revoke,
    };
  });
  const approvalTarget = useApprovalTarget();
  // Seed the initial state from the hook to prevent re-renders.
  const { theme } = GlobalStore.getRawState();
  const { connector } = useAccount();
  console.log(tokensToApprove);
  console.log(requiredSignatures);
  const [isDone, setIsDone] = useState<Array<Record<Token, boolean>>>([]);
  const hasSignatures = GlobalStore.useState(
    s => s.permits.permit2.signature !== undefined || s.permits.permit1.signature !== undefined
  );

  const handleClose = useCallback(() => {
    console.log(`Handling close`, hasSignatures);
    console.log(hasSignatures);
    if (hasSignatures) {
      props.onDone();
    }
  }, [hasSignatures, props]);

  useEffect(() => {
    const areAllApproved = isDone.length === toApprove.length + toRevoke.length;
    const needsSignature = requiredSignatures.length > 0;
    if (areAllApproved) {
      if ((needsSignature && hasSignatures) || !needsSignature) {
        props.onDone();
      }
    }
  }, [
    handleClose,
    isDone.length,
    permitType,
    requiredSignatures.length,
    hasSignatures,
    toApprove.length,
    toRevoke.length,
    props,
  ]);
  const handleStatusChange = useCallback(
    (newStatus: "DONE" | "CUSTOM_APPROVALS", entry: TokenFlow) => {
      if (newStatus === "DONE") {
        setIsDone(prev => {
          return [...prev, { [entry.symbol]: true } as Record<Token, boolean>];
        });
      } else if (newStatus === "CUSTOM_APPROVALS") {
        if (toRevoke.filter(e => e.symbol === entry.symbol).length === 0) {
          // Check if the token needs to move from approve to revoke
          ApprovalStore.update(s => {
            const approve = s.tokensByFlow.approve.filter(
              approval => approval.symbol !== entry.symbol
            );
            const revoke = s.tokensByFlow.revoke.filter(revoke => revoke.symbol !== entry.symbol);
            s.tokensByFlow = {
              approve,
              revoke: [...revoke, entry],
            };
          });
        }
      }
    },
    [setIsDone, toRevoke]
  );

  const areAllApproved = isDone.length === toApprove.length + toRevoke.length;

  return (
    <ApprovalsContainer>
      <ApprovalsHeader />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          align-self: stretch;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
            align-self: stretch;
          `}
        >
          <IconContainer>
            {/*The width of the icon does not correspond to the actual width of the path internally */}
            <SafeIcon width={110} />
          </IconContainer>
          <Text
            shouldScale={false}
            textStyle="Title"
            size="Large"
            textCss={css`
              text-align: center;
            `}
          >
            Manage permissions
          </Text>
        </div>
        <FlexContainer
          css={css`
            display: flex;
            padding: 14px 26px 14px 24px;
            flex-direction: column;
            align-items: flex-start;
            gap: 7px;
            width: 100%;
          `}
        >
          <FlexContainer
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              align-self: stretch;
            `}
          >
            <FlexContainer
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <NumberBadge number={1} isActive={!areAllApproved} />
              <Text
                textStyle={"Title"}
                size={"Small"}
                color={!areAllApproved ? theme.font.primary.default : theme.font.quaternary.default}
              >
                Approve your tokens
              </Text>
            </FlexContainer>
            {!areAllApproved && (
              <Button
                buttonType={"link"}
                navigateTo={NAV_LINKS.DOCUMENTATION_MANAGE_PERMISSIONS}
                text={"Learn more"}
                textSize={"Small"}
                buttonCss={css`
                  padding: 9px 12px;
                `}
              />
            )}
            {areAllApproved && <ApprovedIcon />}
          </FlexContainer>
        </FlexContainer>
        <VisibilityContainer isVisible={!areAllApproved}>
          <MetaMaskTip isVisible={connector?.name === "MetaMask"} />
          {[...toApprove, ...toRevoke].map((entry, index) => {
            return (
              <ApprovalRow
                symbol={entry.symbol}
                approvalTarget={approvalTarget}
                permitType={permitType ?? "permit1"}
                tokenIcon={getTokenIcon(entry.symbol, tokenList)}
                needed={entry.needed}
                onStatusChange={newStatus => handleStatusChange(newStatus, entry)}
                shouldStart={
                  index === 0 ||
                  !!isDone.find(e => {
                    const key = Object.keys(e)[0];
                    const prevRow = toApprove[index - 1];
                    return key === prevRow.symbol && e[key] === true;
                  })
                }
              />
            );
          })}
        </VisibilityContainer>
        <EnableSpendingContainer isVisible={requiredSignatures.length > 0}>
          <div
            css={css`
              display: flex;
              padding: 14px 26px 14px 24px;
              flex-direction: column;
              align-items: flex-start;
              gap: 10px;
              align-self: stretch;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
                flex: 1 0 0;
              `}
            >
              <NumberBadge number={2} isActive={areAllApproved} />
              <Text
                textStyle={"Title"}
                size={"Small"}
                color={areAllApproved ? theme.font.primary.default : theme.font.quaternary.default}
                textCss={css`
                  margin-left: 2px;
                `}
              >
                Enable spending
              </Text>
            </div>
            {areAllApproved && (
              <Text textStyle={"Paragraph"} size={"Large"} color={theme.font.secondary.hover}>
                Grant permission in your wallet to securely trade your tokens.{" "}
                <Link
                  to={NAV_LINKS.DOCUMENTATION_MANAGE_PERMISSIONS}
                  target="_blank"
                  rel="nofollow noreferrer"
                  css={css`
                    text-decoration: none;
                  `}
                >
                  <Text
                    textStyle={"Paragraph"}
                    size={"Large"}
                    color={theme.button.tertiary.text.default}
                    textCss={css`
                      display: inline-block;
                    `}
                  >
                    Learn more...
                  </Text>
                </Link>
              </Text>
            )}
          </div>
        </EnableSpendingContainer>
        <VisibilityContainer isVisible={requiredSignatures.length > 0 && areAllApproved}>
          <SignatureRequest
            permitType={permitType ?? "permit1"}
            approvalTarget={approvalTarget}
            onSignatureDone={handleClose}
          />
        </VisibilityContainer>
      </div>
    </ApprovalsContainer>
  );
};

// Sub-components

const ApprovalsContainer = styled.div`
  width: 100%;
  min-height: 462px;
`;

const NumberBadge: React.FC<{ number: number; isActive: boolean }> = ({ number, isActive }) => {
  return (
    <NumberTextContainer isActive={isActive}>
      <StarIcon style={{ position: "absolute" }} />
      <NumberText>
        <Text
          textStyle={"Title"}
          size={"XS"}
          textCss={css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 25px;
            height: 25px;
          `}
        >
          {number}
        </Text>
      </NumberText>
    </NumberTextContainer>
  );
};

const NumberText = styled.div`
  position: relative;
  color: black;
`;

const NumberTextContainer = styled.div<{ isActive: boolean }>`
  ${props => (props.isActive ? "" : "opacity: 0.4")};
  height: 33px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// Prefer this over isVisible in this case to avoid re-renders here.
export const VisibilityContainer = styled.div<{ isVisible: boolean }>`
  display: ${props => (props.isVisible ? "flex" : "none")};
  flex-direction: column;
  width: 100%;
  gap: 14px;
  padding: 0 10px;
`;

const EnableSpendingContainer = styled(VisibilityContainer)`
  padding: 0;
`;
